/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Autocomplete,
  Grid,
  InputAdornment,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Box,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import { EditProduct } from "./EditProduct";
import { ProductPanelProps } from "../../Utils/Types";
import { endpoints } from "../../Utils/UrlEnum";
import useCustomMemo from "../../Hooks/useCustomMemo";
import {
  InstructorModel,
  OrderTypeModel,
  ProductsModel,
} from "../../Utils/Models";
import { useEffect, useState } from "react";
import { OrderStatusType } from "../../Utils/Utils";
import { AxiosResponse } from "axios";
import { getData } from "../../Services/getData";
import { Controller } from "react-hook-form";
import withRole from "../../Hooks/withRole";

function ProductPanel(props: ProductPanelProps) {
  const { index, control, clearErrors, watch } = props;
  const memo = useCustomMemo();
  const orderTypes = memo?.storage?.orderTypes;
  const workPoints = memo?.storage?.workPoints || [];
  const [filteredProducts, setFilteredProducts] = useState<ProductsModel[]>();
  const [instructors, setInstructors] = useState([] as InstructorModel[]);
  const [selectedWorkPoint, setSelectedWorkPoint] = useState<any>(null);

  /**
   *
   */
  useEffect(() => {
    getUsers();
    if (props?.restrictByWorkPoint) {
      const workPointId = localStorage.getItem("userWorkPointId");
      if (workPointId) {
        selectWorkPoint(workPointId);
      }
    }
  }, []);

  const selectWorkPoint = (value: string) => {
    const workPoint = workPoints?.find((wp: any) => wp._id === value);
    setSelectedWorkPoint(workPoint);
    setValue("client_id.workPointId", JSON.stringify(workPoint));
    // Update price when work point changes
    if (watch && watch("product")) {
      const selectedProduct = watch("product");
      updatePriceForWorkPoint(selectedProduct, workPoint);
    }
  };

  /**
   * Gets the list of instructors and all users (for recommendation purposes).
   * The list of instructors is used to populate the instructor field in the order form.
   * The list of all users is used to populate the recommender field in the order form.
   */
  const getUsers = () => {
    //get the instructors
    getData(
      `${endpoints.instructor.user}/paginated?page=0&perPage=100000`
    ).then((res: AxiosResponse) => {
      if (!res?.data) return;
      setInstructors(res.data?.results);
    });

    // //get all users for recommendation
    // getData(`${endpoints.user.main}`).then((res: AxiosResponse) => {
    //   if (!res?.data) return;
    //   setUsers(res.data);
    // });
  };

  /**
   *
   * @param name
   * @param value
   */
  const setValue = (name: string, value: any, shouldBeDirty?: boolean) => {
    props.setValue(name, value, {
      shouldDirty: shouldBeDirty === false ? false : true,
    });
    props.trigger("product");
  };

  /**
   *
   * @param product
   * @param workPoint
   */
  const updatePriceForWorkPoint = (product: any, workPoint: any) => {
    if (!product || !workPoint || !product.prices) {
      setValue(`product.price`, "");
      return;
    }

    const priceEntry = product.prices.find(
      (price: any) => price.workPointId._id === workPoint._id
    );

    if (priceEntry) {
      setValue(`product.price`, priceEntry.price);
    } else {
      setValue(`product.price`, "");
    }
  };

  /**
   *
   * @param e
   * @param product
   * @param reason
   * @param details
   */
  const handleProductChange = (
    e: any,
    product: any,
    reason: any,
    details: any
  ) => {
    // Transform documentTypes from objects to string IDs
    const transformedProduct = {
      ...details.option,
      documentTypes: details.option?.documentTypes?.map((dt: any) =>
        typeof dt === 'string' ? dt : dt._id
      ) || []
    };

    setValue(`product`, transformedProduct);
    if (clearErrors) clearErrors("product");

    // Update price based on selected work point
    if (selectedWorkPoint && details.option) {
      updatePriceForWorkPoint(details.option, selectedWorkPoint);
    }

    props.trigger("product");
  };

  return (
    <Grid item xs={12} sm={12} md={12} container spacing={5}>
      <Grid item xs={12} sm={12}>
        <Controller
          name="instructor_id"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Autocomplete
              options={instructors ?? []}
              value={
                instructors?.find(
                  (instructor) => instructor._id === field?.value
                ) || undefined
              } // Find the full object by _id
              freeSolo={false}
              disablePortal
              disableClearable
              clearOnBlur={true}
              noOptionsText={Vocabulary.noResultsFound}
              isOptionEqualToValue={(option: any, value: any) => {
                return option?._id == value?._id || option?._id == value;
              }}
              getOptionLabel={(option: any) =>
                `${option?.firstName} ${option?.lastName}`
              }
              onChange={(event, value: any) => {
                field.onChange(value?.instructorDetails[0]?._id);
              }}
              //value={field.name || null} // Ensure the value is controlled
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={field?.value || " "}
                  error={!!error}
                  label={Vocabulary?.instructor} // Label for the input field
                  helperText={error?.message}
                  variant="standard"
                  name="productPanel"
                  type="search"
                />
              )}
            />
          )}
        />
      </Grid>
      {!props?.restrictByWorkPoint && (
        <Grid item xs={12} sm={12}>
          <Controller
            name="workPoint"
            control={control}
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1.5, color: "text.secondary" }}
                >
                  Punct de lucru *
                </Typography>
                <ToggleButtonGroup
                  value={field?.value || null}
                  exclusive
                  onChange={(event, value) => {
                    if (value !== null) {
                      field.onChange(value);
                      selectWorkPoint(value); // Update selected work point
                    }
                  }}
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    "& .MuiToggleButton-root": {
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                      px: 2.5,
                      py: 1,
                      border: "1.5px solid #e0e0e0",
                      color: "text.secondary",
                      "&.Mui-selected": {
                        backgroundColor: "primary.main",
                        color: "white",
                        border: "1.5px solid",
                        borderColor: "primary.main",
                        "&:hover": {
                          backgroundColor: "primary.dark",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "action.hover",
                        borderColor: "primary.main",
                      },
                    },
                  }}
                >
                  {workPoints?.map((workPoint: any) => (
                    <ToggleButton key={workPoint._id} value={workPoint._id}>
                      {workPoint.name}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {error && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {error.message}
                  </Typography>
                )}
              </Box>
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={12}>
        <Controller
          name="orderType"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <Autocomplete
              value={field?.value ?? null}
              disablePortal
              disableClearable
              clearOnBlur={true}
              options={orderTypes ?? []}
              getOptionLabel={(option: OrderTypeModel) => option?.name || ""}
              isOptionEqualToValue={(option, value) =>
                option?.name === value?.name
              }
              onChange={(event, value) => {
                field.onChange(value); // Update field value
                const orderType =
                  value?.name === OrderStatusType?.Certification;
                const products = props?.products?.results?.filter(
                  (product: ProductsModel): product is ProductsModel =>
                    product?.isCertificate === orderType
                );
                setFilteredProducts(products);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  value={field?.value || " "}
                  label={Vocabulary.selectProduct}
                  error={!!error}
                  helperText={error?.message}
                  variant="standard"
                />
              )}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <EditProduct
          control={props.control}
          index={index}
          getValues={props.getValues}
          watch={props?.watch}
          onProductChange={(e, product, reason, details) => {
            handleProductChange(e, product, reason, details);
          }}
          products={filteredProducts}
        />
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          name="product.price"
          control={control}
          render={({ field: { ref, ...field }, fieldState: { error } }) => (
            <TextField
              {...field}
              value={field?.value || ""}
              type="number"
              label={Vocabulary.pret}
              id="product.price"
              fullWidth
              variant="standard"
              error={!!error}
              helperText={
                error?.message ||
                (!selectedWorkPoint
                  ? "Selectează mai întâi punctul de lucru"
                  : "")
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {Vocabulary.currency}
                  </InputAdornment>
                ),
              }}
            />
          )}
        />
        {!watch?.("product.price") &&
          selectedWorkPoint &&
          watch?.("product") && (
            <Typography
              variant="caption"
              color="warning.main"
              sx={{ mt: 1, display: "block" }}
            >
              Prețul nu este disponibil pentru punctul de lucru selectat
            </Typography>
          )}
      </Grid>
    </Grid>
  );
}

export default withRole(ProductPanel);
